import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ColDef } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { PfRequest } from "src/app/generated-models/PfRequest";
import { ApiService } from "src/app/services";
import { LoadingScreenService } from "src/app/shared/loading-screen.service";
import { ModalService } from "src/app/shared/modal.service";
import { getPfSubRequestColumnDefs, getPfSubRequestDefaultColDef } from "./pf-subrequest-grid";

@Component({
  standalone: false,
  templateUrl: "./pf-request-details.component.html",
  styleUrls: ["../../styles.scss", "./pf.scss"],
  styles: [
    `
      .request-details-title {
        padding-bottom: 1rem;
      }

      .request-label {
        font-weight: 700;
        padding-right: 15px;
        text-align: right;
      }

      @media (max-width: 575.98px) {
        .request-label {
          text-align: left;
        }
      }
    `,
  ],
})
export class PfRequestDetailsComponent implements OnInit {
  request: PfRequest;
  approversTooltip = "No PF Admins are currently configured.";
  canApproveOrReject = false;
  isAdmin = false;
  subRequestColumnDefs: ColDef[] = getPfSubRequestColumnDefs();
  subRequestDefaultColDef: ColDef = getPfSubRequestDefaultColDef();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly apiService: ApiService,
    private readonly loadingService: LoadingScreenService,
    private readonly notificationService: ToastrService,
    private readonly modalService: ModalService
  ) {}

  async ngOnInit() {
    const id = Number(this.route.snapshot.paramMap.get("id"));
    if (!Number.isInteger(id) || id <= 0) {
      this.notificationService.error("The PF request ID is invalid.");
      await this.router.navigate(["/pf/ad-hoc-requests/all"]);
      return;
    }

    this.loadingService.setLoading(true);

    try {
      const [request, userProfile, approvers] = await Promise.all([
        this.apiService.getPfRequest(id).toPromise(),
        this.apiService.getUserProfile(),
        this.apiService.getPfRequestApprovers().toPromise(),
      ]);
      this.request = request;
      if (approvers?.length) {
        this.approversTooltip = `PF Admins: ${approvers.join(", ")}`;
      }
      this.isAdmin = userProfile?.IsAdmin ?? false;
      this.canApproveOrReject = this.isAdmin || (userProfile?.IsPfAdmin ?? false);
    } catch (_error) {
      this.notificationService.error(`Failed to load PF request ${id}.`);
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  async viewDemand() {
    if (!this.request?.DemandId) {
      return;
    }

    await this.router.navigate(["/capacity/demand", this.request.DemandId]);
  }

  async approveRequest() {
    await this.processRequestAction(
      "approve",
      () => this.apiService.approvePfRequest(this.request.Id).toPromise(),
      "approved"
    );
  }

  async rejectRequest() {
    await this.processRequestAction(
      "reject",
      () => this.apiService.rejectPfRequest(this.request.Id).toPromise(),
      "rejected"
    );
  }

  async deleteRequest() {
    if (!this.isAdmin || !this.request) {
      return;
    }

    const requestId = this.request.Id;

    try {
      await this.modalService.confirmationModal(
        "You are about to <strong>delete</strong> this PF request.<br>This action cannot be undone. Do you want to continue?"
      );
    } catch {
      return;
    }

    this.loadingService.setLoading(true);

    try {
      await this.apiService.deletePfRequest(requestId).toPromise();
      this.notificationService.success(`PF request ${requestId} deleted successfully.`);
      await this.router.navigate(["/pf/ad-hoc-requests/all"]);
    } catch (_error) {
      this.notificationService.error(`Failed to delete PF request ${requestId}.`);
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  private async processRequestAction(
    actionName: string,
    action: () => Promise<unknown>,
    successVerb: string
  ) {
    if (!this.canApproveOrReject || !this.request) {
      return;
    }

    const requestId = this.request.Id;

    try {
      await this.modalService.confirmationModal(
        `You are about to <strong>${actionName}</strong> this PF request. Do you want to continue?`
      );
    } catch {
      return;
    }

    this.loadingService.setLoading(true);

    try {
      await action();
      this.request = await this.apiService.getPfRequest(requestId).toPromise();
      this.notificationService.success(`PF request ${requestId} ${successVerb} successfully.`);
    } catch (_error) {
      this.notificationService.error(`Failed to ${actionName} PF request ${requestId}.`);
    } finally {
      this.loadingService.setLoading(false);
    }
  }

}
